/**
 * Safe post-login redirect for customer auth (sign-in → OTP → destination).
 */

export const POST_AUTH_REDIRECT_KEY = "toafrica_post_auth_redirect";

/** Well-known creation / onboarding paths (for redirects before sign-in). */
export const AUTH_REDIRECT_PATHS = Object.freeze({
  MATCH_HUB: "/toafrica/matchup",
  FOOTBALL_TEAM_REGISTER: "/toafrica/matchup/football/register",
  BASKETBALL_TEAM_REGISTER: "/toafrica/matchup/basketball/register",
  RUGBY_TEAM_REGISTER: "/toafrica/matchup/rugby/register",
  VOLLEYBALL_TEAM_REGISTER: "/toafrica/matchup/volleyball/register",
  SELLER_SIGNUP: "/toafrica/resale/seller-sign-up",
  FACILITY_OWNER_REGISTER: "/toafrica/facility/owner-registration",
  CUSTOMER_SIGNUP: "/toafrica/account",
});

const ALLOWED_PREFIXES = [
  "/toafrica/",
  "/ipa/",
];

const DEFAULT_FALLBACK = "/toafrica/store";

/**
 * @param {string|null|undefined} redirect
 * @param {string} [fallback]
 * @returns {string}
 */
export function getSafeRedirectPath(redirect, fallback = DEFAULT_FALLBACK) {
  if (!redirect || typeof redirect !== "string") {
    return fallback;
  }

  const trimmed = redirect.trim();
  if (!trimmed.startsWith("/") || trimmed.startsWith("//")) {
    return fallback;
  }
  if (trimmed.includes(":")) {
    return fallback;
  }

  const pathOnly = trimmed.split("?")[0].split("#")[0];
  const allowed = ALLOWED_PREFIXES.some((prefix) => pathOnly.startsWith(prefix));
  return allowed ? trimmed : fallback;
}

/**
 * @param {string|null|undefined} path
 */
export function persistPostAuthRedirect(path) {
  const safe = getSafeRedirectPath(path, null);
  if (safe) {
    sessionStorage.setItem(POST_AUTH_REDIRECT_KEY, safe);
  }
}

/**
 * @param {{ queryRedirect?: string|null, fallback?: string }} [options]
 * @returns {string}
 */
export function resolvePostAuthRedirect(options = {}) {
  const { queryRedirect, fallback = DEFAULT_FALLBACK } = options;
  const stored = sessionStorage.getItem(POST_AUTH_REDIRECT_KEY);
  sessionStorage.removeItem(POST_AUTH_REDIRECT_KEY);
  return getSafeRedirectPath(queryRedirect || stored, fallback);
}

/**
 * @param {import('vue-router').Router} router
 * @param {string|import('vue-router').RouteLocationRaw} location
 * @returns {string|null}
 */
export function redirectPathForRoute(router, location) {
  try {
    return router.resolve(location).fullPath;
  } catch {
    return null;
  }
}

/**
 * @param {string|null|undefined} redirectPath
 * @returns {{ name: string, query?: { redirect: string } }}
 */
export function buildSignInLocation(redirectPath) {
  const safe = getSafeRedirectPath(redirectPath, null);
  if (safe) {
    persistPostAuthRedirect(safe);
  }
  return {
    name: "CustomerSignIn",
    ...(safe ? { query: { redirect: safe } } : {}),
  };
}

/**
 * @param {import('vue-router').Router} router
 * @param {string|null|undefined} redirectPath
 */
export function navigateToSignIn(router, redirectPath) {
  return router.push(buildSignInLocation(redirectPath));
}
